import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    HOST_TAG_NAME,
    ViewEncapsulation,
    computed,
    contentChildren,
    effect,
    inject,
    input,
    model,
    signal,
} from '@angular/core';

import { DS_ATOMS } from '@frontend/ui/shared';

export const DS_PILL_VARIANTS_ARRAY = ['current', 'subtle', 'strong'] as const;
export type DsPillVariant = (typeof DS_PILL_VARIANTS_ARRAY)[number];

export const DS_PILL_SIZE_ARRAY = ['small', 'medium'] as const;
export type DsPillSize = (typeof DS_PILL_SIZE_ARRAY)[number];

@Component({
    selector: 'button[ds-pill], a[ds-pill]',
    template: `
        <ng-content select="[slot=start]" />
        <span class="ds-pill-text">
            @if (label()) {
                {{ label() }}
            } @else {
                <ng-content />
            }
        </span>
        <ng-content select="[slot=end]" />
        <span class="ds-button-touch-target" aria-hidden="true"></span>
    `,
    host: {
        'class': 'ds-pill',
        '[class]': 'hostClass()',
        '[class.ds-pill-selected]': 'selected()',
        '[class.ds-pill-disabled]': 'disabled()',
        '[attr.disabled]': '_anchorPill ? null : (disabled() ? "" : null)',
        '[class.ds-pill-rounded-padding]': 'roundedPadding()',
        '[class.ds-pill-inverse]': 'inverse()',
        '[attr.aria-disabled]': 'disabled()',
        '[attr.tabindex]': 'disabled() ? -1 : 0',
        '(keydown)': 'onKeydown($event)',
    },
    styleUrl: 'pill.component.scss',
    standalone: true,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DsPill {
    public readonly selected = input(false);
    public readonly variant = model<DsPillVariant>('current');
    public readonly size = model<DsPillSize>('medium');
    public readonly disabled = input(false);
    public readonly inverse = model(false);
    public readonly label = input<string>('');

    protected readonly hostClass = computed(() => `ds-pill-${this.variant()} ds-pill-${this.size()}`);

    //TO DO use signal-based content queries
    /**
     * Retrieves and holds the list of child components or directives that implement the `DsAtoms` interface when
     * they are projected into the current component's content slots and are instances of the injection token `DS_ATOMS`.
     * If there are any children that implement `DsAtoms` interface are present, the host class `ds-pill-rounded-padding`
     * is added to the `ds-pill` component.
     */

    private readonly elementRef = inject(ElementRef<HTMLElement>);
    private readonly tagName = inject(HOST_TAG_NAME);
    private readonly _anchorPill = this.tagName === 'a';

    protected readonly dsAtoms = contentChildren(DS_ATOMS, {
        descendants: true,
        read: ElementRef,
    });

    protected readonly roundedPadding = signal(false);

    constructor() {
        effect(() => {
            const atoms = this.dsAtoms();

            // Delay the check until after DOM updates (directive bindings like hidden)
            requestAnimationFrame(() => {
                const hasVisibleNotification = atoms.some((item) => {
                    const el = item.nativeElement;
                    return el.tagName.toLowerCase() === 'ds-notification-bubble' && !el.hasAttribute?.('hidden');
                });

                this.roundedPadding.set(hasVisibleNotification);
            });
        });
    }

    onKeydown(event: KeyboardEvent) {
        const { key } = event;
        if (key === ' ' || key === 'Enter') {
            //checks if anchor tag is used as a pill, if true, prevents default scrolling when using space as navigation tool
            if (this._anchorPill) {
                event.preventDefault();
                (this.elementRef as ElementRef<HTMLAnchorElement>).nativeElement.click();
            }
        }
    }
}
